import { useState, useEffect, useCallback } from "react";
import { Globe, Check, X, Copy, ArrowUpRight, RefreshCw, AlertCircle, CheckCircle2, Clock, Link2, Crown, Zap, Camera } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const TIER_INFO = {
  vanity: { label: "Free URL", color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/20" },
  subdomain: { label: "Custom Subdomain", color: "text-purple-400", bg: "bg-purple-500/10", border: "border-purple-500/20" },
  custom: { label: "Custom Domain", color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/20" },
};

const STATUS_STYLES = {
  pending: { icon: Clock, color: "text-yellow-400", bg: "bg-yellow-500/10", label: "Pending Review" },
  in_progress: { icon: RefreshCw, color: "text-blue-400", bg: "bg-blue-500/10", label: "In Progress" },
  awaiting_dns: { icon: AlertCircle, color: "text-orange-400", bg: "bg-orange-500/10", label: "Awaiting Your DNS Update" },
  approved: { icon: CheckCircle2, color: "text-green-400", bg: "bg-green-500/10", label: "Approved" },
  active: { icon: CheckCircle2, color: "text-emerald-400", bg: "bg-emerald-500/10", label: "Active & Live" },
  rejected: { icon: X, color: "text-red-400", bg: "bg-red-500/10", label: "Rejected" },
};

const HealthBadge = ({ domain, health, loading, onCheck }) => {
  if (loading) return <RefreshCw className="w-3.5 h-3.5 text-white/20 animate-spin" />;
  if (!health) return (
    <button onClick={onCheck} className="text-white/20 hover:text-white/50 transition" title="Check DNS health" data-testid={`health-check-${domain}`}>
      <AlertCircle className="w-3.5 h-3.5" />
    </button>
  );

  const allGood = health.dns_resolves && health.http_reachable && health.ssl_valid;
  const partial = health.dns_resolves && !health.ssl_valid;

  return (
    <button onClick={onCheck} title={allGood ? "Healthy — DNS, HTTP & SSL verified" : partial ? "DNS resolves but SSL/HTTP issues" : "DNS not resolving"} data-testid={`health-badge-${domain}`}>
      {allGood ? (
        <CheckCircle2 className="w-4 h-4 text-green-400" />
      ) : partial ? (
        <AlertCircle className="w-4 h-4 text-yellow-400" />
      ) : (
        <X className="w-4 h-4 text-red-400" />
      )}
    </button>
  );
};

const HealthDetailCard = ({ domain, health, loading, onCheck }) => {
  if (!health && !loading) return null;

  const checks = health ? [
    { label: "DNS Resolution", ok: health.dns_resolves, detail: health.ip_address ? `Resolves to ${health.ip_address}` : "Not resolving" },
    { label: "HTTP Reachable", ok: health.http_reachable, detail: health.http_reachable ? "Server responding" : "Connection failed" },
    { label: "SSL Certificate", ok: health.ssl_valid, detail: health.ssl_valid ? "Valid HTTPS" : "No SSL or invalid certificate" },
    { label: "Points to Platform", ok: health.points_to_platform, detail: health.points_to_platform ? "DJsandMCMedia detected" : "Not pointing to platform yet" },
  ] : [];

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4 space-y-3" data-testid={`health-detail-${domain}`}>
      <div className="flex items-center justify-between">
        <p className="text-white/50 text-xs font-bold uppercase tracking-wider">Domain Health — {domain}</p>
        <button onClick={onCheck} className="text-white/30 hover:text-white transition" disabled={loading}>
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-white/20 text-xs"><RefreshCw className="w-3 h-3 animate-spin" /> Running health check...</div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {checks.map(c => (
            <div key={c.label} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${c.ok ? "bg-green-500/5 border border-green-500/10" : "bg-red-500/5 border border-red-500/10"}`}>
              {c.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400 flex-shrink-0" /> : <X className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />}
              <div>
                <p className="text-white text-xs font-medium">{c.label}</p>
                <p className="text-white/25 text-[10px]">{c.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      {health && <p className="text-white/15 text-[10px]">Last checked: {new Date(health.checked_at).toLocaleTimeString()}</p>}
    </div>
  );
};

export const DomainsTab = ({ currentUser, authToken }) => {
  const [domainData, setDomainData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subdomainInput, setSubdomainInput] = useState("");
  const [subdomainAvail, setSubdomainAvail] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customDomain, setCustomDomain] = useState("");
  const [customNotes, setCustomNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [copied, setCopied] = useState("");
  const [healthData, setHealthData] = useState({});
  const [healthLoading, setHealthLoading] = useState({});

  const flash = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 4000);
  };

  const fetchDomain = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/domains/my-domain`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setDomainData(data);
        if (data.subdomain) setSubdomainInput(data.subdomain);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authToken]);

  useEffect(() => { fetchDomain(); }, [fetchDomain]);

  const checkAvailability = async (sub) => {
    if (!sub || sub.length < 3) { setSubdomainAvail(null); return; }
    setChecking(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/domains/check-subdomain/${encodeURIComponent(sub)}`);
      if (res.ok) setSubdomainAvail(await res.json());
    } catch (e) {}
    finally { setChecking(false); }
  };

  const handleSubdomainChange = (val) => {
    const clean = val.toLowerCase().replace(/[^a-z0-9-]/g, "");
    setSubdomainInput(clean);
    setSubdomainAvail(null);
    if (clean.length >= 3) {
      const timer = setTimeout(() => checkAvailability(clean), 500);
      return () => clearTimeout(timer);
    }
  };

  const saveSubdomain = async () => {
    if (!subdomainInput || subdomainInput.length < 3) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/domains/subdomain`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ subdomain: subdomainInput }),
      });
      if (res.ok) {
        const data = await res.json();
        const dnsMsg = data.dns_provisioned
          ? "Subdomain saved & DNS auto-provisioned!"
          : "Subdomain saved! DNS will propagate shortly.";
        flash(dnsMsg);
        fetchDomain();
      } else {
        const d = await res.json();
        flash(d.detail || "Failed to save", "error");
      }
    } catch (e) { flash(e.message, "error"); }
    finally { setSaving(false); }
  };

  const submitCustomDomain = async () => {
    if (!customDomain.trim()) { flash("Enter a domain name", "error"); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/domains/custom-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ domain: customDomain.trim().toLowerCase(), notes: customNotes }),
      });
      if (res.ok) {
        flash("Domain request submitted! You'll receive an email when reviewed.");
        setCustomDomain("");
        setCustomNotes("");
        fetchDomain();
      } else {
        const d = await res.json();
        flash(d.detail || "Failed to submit", "error");
      }
    } catch (e) { flash(e.message, "error"); }
    finally { setSubmitting(false); }
  };

  const uploadDnsScreenshot = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${BACKEND_URL}/api/domains/dns-screenshot`, {
        method: "POST",
        headers: { Authorization: `Bearer ${authToken}` },
        body: formData,
      });
      if (res.ok) {
        flash("DNS screenshot uploaded. We'll review it shortly.");
        fetchDomain();
      } else flash("Upload failed", "error");
    } catch (e) { flash(e.message, "error"); }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(url);
      setTimeout(() => setCopied(""), 2000);
    });
  };

  const checkHealth = async (domain) => {
    if (!domain) return;
    setHealthLoading(prev => ({ ...prev, [domain]: true }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/domains/health/${encodeURIComponent(domain)}`);
      if (res.ok) {
        const data = await res.json();
        setHealthData(prev => ({ ...prev, [domain]: data }));
      }
    } catch (e) {}
    finally { setHealthLoading(prev => ({ ...prev, [domain]: false })); }
  };

  // Auto-check health on load when domains exist
  useEffect(() => {
    if (!domainData) return;
    if (domainData.subdomain_url) checkHealth(domainData.subdomain_url);
    if (domainData.custom_domain) checkHealth(domainData.custom_domain);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainData?.subdomain_url, domainData?.custom_domain]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 text-white/20 animate-spin" />
      </div>
    );
  }

  const d = domainData || {};
  const tier = d.tier || "vanity";
  const tierInfo = TIER_INFO[tier];
  const canSubdomain = tier === "subdomain" || tier === "custom";
  const canCustom = tier === "custom";
  const baseUrl = "djsandmcmedia.com";

  return (
    <div className="space-y-6 p-6" data-testid="domains-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Globe className="w-5 h-5 text-purple-400" /> Domain Settings
          </h3>
          <p className="text-white/40 text-sm mt-1">
            Manage your station's web address
          </p>
        </div>
        <div className={`px-3 py-1.5 rounded-xl text-xs font-bold ${tierInfo.bg} ${tierInfo.border} border ${tierInfo.color}`}>
          {tierInfo.label}
        </div>
      </div>

      {message.text && (
        <div className={`px-4 py-2.5 rounded-xl text-sm font-medium ${
          message.type === "error" ? "bg-red-500/15 text-red-300 border border-red-500/20" : "bg-green-500/15 text-green-300 border border-green-500/20"
        }`}>{message.text}</div>
      )}

      {/* ═══ CURRENT URLS ═══ */}
      <div className="bg-white/[0.03] border border-white/8 rounded-2xl p-5 space-y-4" data-testid="domains-current">
        <h4 className="text-white/60 text-xs font-bold uppercase tracking-widest">Your Station URLs</h4>

        {/* Vanity URL — always available */}
        {d.vanity_url && (
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Link2 className="w-4 h-4 text-blue-400" />
              </div>
              <div className="min-w-0">
                <p className="text-white/30 text-[10px] uppercase tracking-wider font-bold">Free URL</p>
                <p className="text-white text-sm font-medium truncate">{baseUrl}{d.vanity_url}</p>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => copyUrl(`https://${baseUrl}${d.vanity_url}`)}
                className="p-2 text-white/30 hover:text-white rounded-lg hover:bg-white/5 transition" data-testid="copy-vanity-url">
                {copied === `https://${baseUrl}${d.vanity_url}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </button>
              <a href={`${d.vanity_url}`} target="_blank" rel="noopener noreferrer"
                className="p-2 text-white/30 hover:text-white rounded-lg hover:bg-white/5 transition">
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        )}

        {/* Subdomain — Pro+ */}
        {d.subdomain_url && (
          <div className="flex items-center justify-between py-2 border-t border-white/5">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                <Globe className="w-4 h-4 text-purple-400" />
              </div>
              <div className="min-w-0">
                <p className="text-white/30 text-[10px] uppercase tracking-wider font-bold">Subdomain</p>
                <p className="text-white text-sm font-medium truncate">{d.subdomain_url}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <HealthBadge domain={d.subdomain_url} health={healthData[d.subdomain_url]} loading={healthLoading[d.subdomain_url]} onCheck={() => checkHealth(d.subdomain_url)} />
              <button onClick={() => copyUrl(`https://${d.subdomain_url}`)}
                className="p-2 text-white/30 hover:text-white rounded-lg hover:bg-white/5 transition" data-testid="copy-subdomain-url">
                {copied === `https://${d.subdomain_url}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}

        {/* Custom domain — Premium */}
        {d.custom_domain && (
          <div className="flex items-center justify-between py-2 border-t border-white/5">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
                <Crown className="w-4 h-4 text-yellow-400" />
              </div>
              <div className="min-w-0">
                <p className="text-white/30 text-[10px] uppercase tracking-wider font-bold">Custom Domain</p>
                <p className="text-white text-sm font-medium truncate">{d.custom_domain}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <HealthBadge domain={d.custom_domain} health={healthData[d.custom_domain]} loading={healthLoading[d.custom_domain]} onCheck={() => checkHealth(d.custom_domain)} />
              <button onClick={() => copyUrl(`https://${d.custom_domain}`)}
                className="p-2 text-white/30 hover:text-white rounded-lg hover:bg-white/5 transition">
                {copied === `https://${d.custom_domain}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ═══ HEALTH DETAIL CARDS ═══ */}
      {(d.subdomain_url || d.custom_domain) && (
        <div className="space-y-3">
          {d.subdomain_url && (healthData[d.subdomain_url] || healthLoading[d.subdomain_url]) && (
            <HealthDetailCard domain={d.subdomain_url} health={healthData[d.subdomain_url]} loading={healthLoading[d.subdomain_url]} onCheck={() => checkHealth(d.subdomain_url)} />
          )}
          {d.custom_domain && (healthData[d.custom_domain] || healthLoading[d.custom_domain]) && (
            <HealthDetailCard domain={d.custom_domain} health={healthData[d.custom_domain]} loading={healthLoading[d.custom_domain]} onCheck={() => checkHealth(d.custom_domain)} />
          )}
        </div>
      )}

      {/* ═══ SUBDOMAIN PICKER ═══ */}
      {canSubdomain ? (
        <div className="bg-white/[0.03] border border-purple-500/15 rounded-2xl p-5 space-y-4" data-testid="domains-subdomain-picker">
          <h4 className="text-white font-semibold text-sm flex items-center gap-2">
            <Globe className="w-4 h-4 text-purple-400" />
            Custom Subdomain
          </h4>
          <p className="text-white/35 text-xs">Choose your branded subdomain — changes take effect immediately.</p>

          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <input
                value={subdomainInput}
                onChange={e => handleSubdomainChange(e.target.value)}
                placeholder="mystation"
                maxLength={32}
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500/40 pr-40"
                data-testid="subdomain-input"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/20 text-sm">.{baseUrl}</span>
            </div>
          </div>

          {subdomainInput.length >= 3 && (
            <div className="flex items-center gap-2 text-xs">
              {checking ? (
                <span className="text-white/30 flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Checking...</span>
              ) : subdomainAvail?.available ? (
                <span className="text-green-400 flex items-center gap-1"><Check className="w-3 h-3" /> Available!</span>
              ) : subdomainAvail ? (
                <span className="text-red-400 flex items-center gap-1"><X className="w-3 h-3" /> {subdomainAvail.reason}</span>
              ) : null}
            </div>
          )}

          <button
            onClick={saveSubdomain}
            disabled={saving || !subdomainInput || subdomainInput.length < 3 || (subdomainAvail && !subdomainAvail.available)}
            className="bg-purple-500 hover:bg-purple-600 disabled:opacity-40 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition"
            data-testid="subdomain-save"
          >
            {saving ? "Saving..." : d.subdomain ? "Update Subdomain" : "Claim Subdomain"}
          </button>
        </div>
      ) : (
        <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-5" data-testid="domains-subdomain-locked">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center flex-shrink-0">
              <Zap className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm">Branded Subdomain</h4>
              <p className="text-white/35 text-xs mt-1">Get your own <span className="text-purple-400">yourstation.djsandmcmedia.com</span> subdomain with a Pro plan.</p>
              <p className="text-purple-400/60 text-xs mt-2 font-medium">Included with Pro plan</p>
            </div>
          </div>
        </div>
      )}

      {/* ═══ CUSTOM DOMAIN ═══ */}
      {canCustom ? (
        <div className="bg-white/[0.03] border border-yellow-500/15 rounded-2xl p-5 space-y-4" data-testid="domains-custom-form">
          <h4 className="text-white font-semibold text-sm flex items-center gap-2">
            <Crown className="w-4 h-4 text-yellow-400" />
            Custom Domain
          </h4>

          {d.pending_request ? (
            <div className="space-y-3">
              {(() => {
                const st = STATUS_STYLES[d.pending_request.status] || STATUS_STYLES.pending;
                const Icon = st.icon;
                return (
                  <div className={`flex items-center justify-between px-4 py-3 rounded-xl ${st.bg} border border-white/5`}>
                    <div className="flex items-center gap-3">
                      <Icon className={`w-5 h-5 ${st.color}`} />
                      <div>
                        <p className="text-white text-sm font-medium">{d.pending_request.domain}</p>
                        <p className={`text-xs ${st.color}`}>{st.label}</p>
                      </div>
                    </div>
                    {d.pending_request.estimated_hours && (
                      <p className="text-white/30 text-xs">Est. setup: ~{d.pending_request.estimated_hours}h</p>
                    )}
                  </div>
                );
              })()}

              {d.pending_request.admin_notes && (
                <p className="text-white/40 text-xs bg-white/[0.03] px-4 py-2.5 rounded-xl">{d.pending_request.admin_notes}</p>
              )}

              {/* DNS Instructions with Copy */}
              {d.pending_request.dns_instructions && (
                <div className="bg-black/30 border border-white/5 rounded-xl p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-white/50 text-xs font-bold uppercase tracking-wider">DNS Instructions</p>
                    <button
                      onClick={() => { navigator.clipboard.writeText(d.pending_request.dns_instructions); flash("DNS records copied!"); }}
                      className="flex items-center gap-1 text-purple-400 text-xs hover:text-purple-300 transition"
                      data-testid="copy-dns-records"
                    >
                      <Copy className="w-3 h-3" /> Copy DNS Records
                    </button>
                  </div>
                  <pre className="text-white/70 text-xs whitespace-pre-wrap font-mono leading-relaxed">{d.pending_request.dns_instructions}</pre>
                </div>
              )}

              {/* Upload DNS Screenshot */}
              {["awaiting_dns", "pending", "in_progress"].includes(d.pending_request.status) && (
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-purple-400 text-xs px-3 py-2 rounded-lg bg-purple-500/10 hover:bg-purple-500/15 cursor-pointer transition" data-testid="upload-dns-screenshot">
                    <Camera className="w-3.5 h-3.5" /> Upload DNS Screenshot
                    <input type="file" accept="image/*" className="hidden" onChange={uploadDnsScreenshot} />
                  </label>
                  <span className="text-white/20 text-xs">Show us your DNS is configured</span>
                </div>
              )}

              {/* Show uploaded screenshots */}
              {d.pending_request.dns_screenshots?.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-white/30">
                  <Camera className="w-3 h-3" />
                  {d.pending_request.dns_screenshots.length} screenshot{d.pending_request.dns_screenshots.length !== 1 ? "s" : ""} uploaded
                </div>
              )}
            </div>
          ) : d.custom_domain ? (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-green-500/10 border border-green-500/20">
              <CheckCircle2 className="w-5 h-5 text-green-400" />
              <div>
                <p className="text-white text-sm font-medium">{d.custom_domain}</p>
                <p className="text-green-400 text-xs">Active</p>
              </div>
            </div>
          ) : (
            <>
              <p className="text-white/35 text-xs">Request a custom domain — we'll handle the setup and send you DNS instructions.</p>
              <input
                value={customDomain}
                onChange={e => setCustomDomain(e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, ""))}
                placeholder="mybrandradio.com"
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-yellow-500/40"
                data-testid="custom-domain-input"
              />
              <textarea
                value={customNotes}
                onChange={e => setCustomNotes(e.target.value)}
                placeholder="Any notes (e.g., registrar you're using, preferred setup date)"
                rows={2}
                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-yellow-500/40 resize-none"
                data-testid="custom-domain-notes"
              />
              <button
                onClick={submitCustomDomain}
                disabled={submitting || !customDomain.trim()}
                className="bg-yellow-500 hover:bg-yellow-600 disabled:opacity-40 text-black px-5 py-2.5 rounded-xl text-sm font-bold transition"
                data-testid="custom-domain-submit"
              >
                {submitting ? "Submitting..." : "Request Domain Setup"}
              </button>
            </>
          )}

          {d.custom_domain_dns && (
            <div className="bg-black/30 border border-white/5 rounded-xl p-4 space-y-2">
              <p className="text-white/50 text-xs font-bold uppercase tracking-wider">DNS Instructions</p>
              <pre className="text-white/70 text-xs whitespace-pre-wrap font-mono leading-relaxed">{d.custom_domain_dns}</pre>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-5" data-testid="domains-custom-locked">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center flex-shrink-0">
              <Crown className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <h4 className="text-white font-semibold text-sm">Custom Domain</h4>
              <p className="text-white/35 text-xs mt-1">Connect your own domain like <span className="text-yellow-400">mybrandradio.com</span> with a Premium plan.</p>
              <p className="text-yellow-400/60 text-xs mt-2 font-medium">Included with Premium plan</p>
            </div>
          </div>
        </div>
      )}

      {/* ═══ PLAN COMPARISON ═══ */}
      <div className="bg-white/[0.02] border border-white/5 rounded-2xl p-5">
        <h4 className="text-white/40 text-xs font-bold uppercase tracking-widest mb-4">Domain Features by Plan</h4>
        <div className="grid grid-cols-3 gap-3">
          {[
            { plan: "Starter", price: "$19.99/mo", domain: `${baseUrl}/station/slug`, color: "blue", current: d.plan === "starter" },
            { plan: "Pro", price: "$29.99/mo", domain: `brand.${baseUrl}`, color: "purple", current: d.plan === "professional" },
            { plan: "Premium", price: "$79.99/mo", domain: "yourdomain.com", color: "yellow", current: ["premium", "enterprise",].includes(d.plan) },
          ].map(p => (
            <div key={p.plan} className={`rounded-xl p-3 border ${p.current ? `border-${p.color}-500/30 bg-${p.color}-500/5` : "border-white/5 bg-white/[0.02]"}`}>
              <p className={`text-${p.color}-400 text-xs font-bold uppercase`}>{p.plan}</p>
              <p className="text-white/50 text-[10px] mt-0.5">{p.price}</p>
              <p className="text-white text-xs font-medium mt-2 truncate">{p.domain}</p>
              {p.current && <p className="text-green-400 text-[10px] mt-1 font-medium">Current Plan</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
